'use client';

import { cn } from '@core/utils';
import { Root, Thumb } from '@radix-ui/react-switch';
import { cva } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';

const switchRootVariants = cva(
  'relative rounded-pill transition-colors duration-200 data-[state=checked]:bg-Gray-black data-[state=unchecked]:bg-Gray-300',
  {
    variants: {
      size: {
        small: 'h-24 w-40',
        large: 'h-30 w-50',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
      },
    },
    defaultVariants: {
      size: 'small',
    },
  },
);

const switchThumbVariants = cva(
  'block rounded-pill bg-Gray-white transition-transform duration-200',
  {
    variants: {
      size: {
        small:
          'size-20 data-[state=checked]:translate-x-[18px] data-[state=unchecked]:translate-x-[2px]',
        large:
          'size-26 data-[state=checked]:translate-x-[22px] data-[state=unchecked]:translate-x-[2px]',
      },
    },
  },
);

interface SwitchProps extends Omit<
  ComponentPropsWithoutRef<typeof Root>,
  'onChange' | 'onCheckedChange'
> {
  checked?: boolean;
  disabled?: boolean;
  size?: 'small' | 'large';
  onChange?: (checked: boolean) => void;
}

export const Switch = ({
  checked = false,
  onChange,
  disabled = false,
  size = 'small',
  className,
  ...props
}: SwitchProps) => {
  return (
    <div className={cn('inline-flex items-center gap-8', className)}>
      <Root
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className={switchRootVariants({ size, disabled })}
        {...props}
      >
        <Thumb className={switchThumbVariants({ size })} />
      </Root>
    </div>
  );
};
